import { useState } from "react";
import { formatDate, formatTime } from "../format.js";
import {
  recurrenceFundingSummary,
  recurringLessons,
  type RecurrenceBookingResponse,
  type RecurrenceOccurrence,
} from "./recurrences.js";

function statusLabel(status: string): string {
  return status === "booked" ? "booked" : status === "failed" ? "failed" : status.replace(/_/g, " ");
}

/**
 * What happened to every lesson in a series, with a retry that touches only the
 * ones that failed. Booked lessons are never sent again: the request names the
 * failed occurrence ids, and the booking service keeps one idempotency key per
 * occurrence, so a repeated click cannot book a lesson twice either.
 */
export function RecurringSeriesOutcome({ result }: { result: RecurrenceBookingResponse }) {
  const [occurrences, setOccurrences] = useState<RecurrenceOccurrence[]>(result.occurrences);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const funding = recurrenceFundingSummary(occurrences);
  const count = (status: string) => occurrences.filter((occurrence) => occurrence.status === status).length;
  const failed = occurrences.filter((occurrence) => occurrence.status === "failed");

  async function retryFailed() {
    setBusy(true);
    setProblem(null);
    try {
      const detail = await recurringLessons.retry(result.plan.id, failed.map((occurrence) => occurrence.id));
      setOccurrences(detail.occurrences);
    } catch (cause) {
      setProblem(
        cause instanceof Error
          ? cause.message
          : "The failed lessons could not be retried. Check the lessons before trying again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <p>
        <strong>
          {count("booked")} booked · {count("unavailable")} unavailable · {failed.length} failed
        </strong>
      </p>
      <p className="small">
        Funding recorded by the booking system: {funding.creditFunded}{" "}
        credit-funded · {funding.paymentPending} payment pending
        {funding.notFundedYet > 0 ? ` · ${funding.notFundedYet} not created/funded yet` : ""}.
      </p>
      {funding.paymentPending > 0 && (
        <p className="small">
          Payment-pending lessons stay pending until they are settled. Once
          a lesson is past and still unpaid, it uses the normal unpaid-lessons
          flow; no separate recurring debt list is created.
        </p>
      )}
      <ul className="small" aria-label="Each lesson in the series">
        {occurrences.map((occurrence) => (
          <li key={occurrence.id}>
            {formatDate(occurrence.starts_at)} at {formatTime(occurrence.starts_at)} — {statusLabel(occurrence.status)}
            {occurrence.failure_reason && occurrence.status !== "booked"
              ? `: ${occurrence.failure_reason}`
              : ""}
          </li>
        ))}
      </ul>
      {failed.length > 0 && (
        <p>
          <button type="button" className="quiet small" disabled={busy} onClick={() => void retryFailed()}>
            {busy ? "Retrying…" : "Retry failed lessons"}
          </button>
          <span className="small muted"> Only the {failed.length === 1 ? "1 failed lesson is" : `${failed.length} failed lessons are`} retried; lessons already booked are left alone.</span>
        </p>
      )}
      {problem && <p className="error" role="alert">{problem}</p>}
    </>
  );
}
